import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  Field,
  LoadingRegion,
  Notice,
  Status,
  VENUE_MAP_VERSION_STATES,
  presentation,
} from '@gatecheckpro/design-system';
import {
  topologyCodeSchema,
  type SectionKind,
  type VenueMapDetail,
  type VenueMapVersionTopology,
} from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useTopologyDraft, type DraftStatus } from '../../lib/venue-map/useTopologyDraft.ts';
import {
  addRowBlock,
  addSeatGroup,
  addSection,
  addTable,
  removeSection,
} from '../../lib/venue-map/topology.ts';
import { VenueMapCanvas } from '@gatecheckpro/venue-map';

/**
 * Drawing a room.
 *
 * The editor holds a working document in the browser, and says so. That is
 * allowed: round-tripping every keystroke would be unusable at a venue on a bad
 * connection. What is not allowed is letting a person mistake a proposal for
 * the record, so the state of the draft is stated on screen at all times and
 * every save is conditional on the digest this editor last saw.
 *
 * Nothing merges. A whole-document import that combined two people's edits
 * would produce a seating plan neither of them drew, and the person who found
 * out would be an attendee standing in front of a chair that does not exist.
 */
export function VenueMapEditorPage() {
  const { organizationId, versionId } = useParams<{ organizationId: string; versionId: string }>();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const versionPath = `${base}/venue-map-versions/${versionId ?? ''}`;
  const { resource } = useResource<VenueMapVersionTopology>(versionPath);

  if (resource.status === 'loading') return <LoadingRegion label="Loading the room" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  return (
    <VenueMapEditor
      base={base}
      versionPath={versionPath}
      organizationId={organizationId ?? ''}
      loaded={resource.data}
      key={resource.data.version.venueMapVersionId}
    />
  );
}

/** What each draft state means, and what a person does about it. */
const DRAFT_STATE: Readonly<
  Record<
    DraftStatus,
    {
      label: string;
      tone: 'positive' | 'caution' | 'critical' | 'info' | 'neutral';
      glyph: string;
      meaning: string;
    }
  >
> = {
  persisted: {
    label: 'Saved',
    tone: 'positive',
    glyph: '✓',
    meaning: 'What you are looking at is what GateCheckPro has.',
  },
  unsaved: {
    label: 'Not saved',
    tone: 'caution',
    glyph: '●',
    meaning: 'These edits are in this browser only. Nothing else can see them yet.',
  },
  saving: {
    label: 'Saving',
    tone: 'info',
    glyph: '→',
    meaning: 'Sending the room to GateCheckPro.',
  },
  failed: {
    label: 'Not saved',
    tone: 'critical',
    glyph: '✕',
    meaning: 'The save was refused. Your edits are still here.',
  },
  stale: {
    label: 'Someone else saved first',
    tone: 'critical',
    glyph: '⚠',
    meaning:
      'This draft moved on while you were working. Your edits are still here and will not be written over theirs.',
  },
};

function VenueMapEditor({
  base,
  versionPath,
  organizationId,
  loaded,
}: {
  readonly base: string;
  readonly versionPath: string;
  readonly organizationId: string;
  readonly loaded: VenueMapVersionTopology;
}) {
  const draft = useTopologyDraft(loaded, versionPath);
  const map = useResource<VenueMapDetail>(`${base}/venue-maps/${loaded.venueMapId}`);
  const publish = useCommand<VenueMapDetail>();

  const [sectionCode, setSectionCode] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [sectionKind, setSectionKind] = useState<SectionKind>('SEATED');
  const [rowCount, setRowCount] = useState('10');
  const [seatsPerRow, setSeatsPerRow] = useState('20');
  const [tableCode, setTableCode] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [target, setTarget] = useState('');

  const editable = loaded.version.lifecycle === 'DRAFT';
  const state = DRAFT_STATE[draft.status];
  const lifecycle = presentation(VENUE_MAP_VERSION_STATES, loaded.version.lifecycle);
  const codeProblem =
    sectionCode === '' || topologyCodeSchema.safeParse(sectionCode).success
      ? undefined
      : 'Capitals, numbers and underscores.';
  /**
   * What a section is decides what can go in it, and the server enforces that:
   * a chair at a table in a SEATED section is refused, by the API and by a
   * constraint underneath it. So the forms offer only the sections that could
   * accept what they add, rather than letting somebody build a room that will
   * be rejected on save.
   */
  const seatedSections = draft.document.sections.filter((section) => section.kind === 'SEATED');
  const tableSections = draft.document.sections.filter((section) => section.kind === 'TABLE');
  const activeSection = target === '' ? draft.document.sections[0]?.code : target;

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-stack gc-stack--tight">
        <span className="gc-eyebrow">
          <Link to={`/o/${organizationId}/venue-maps/${loaded.venueMapId}`}>
            {loaded.venueMapCode}
          </Link>
        </span>
        <div className="gc-row gc-row--baseline">
          <h2>Version {loaded.version.versionNumber}</h2>
          <Status
            tone={lifecycle.tone}
            glyph={lifecycle.glyph}
            label={lifecycle.label}
            title={lifecycle.meaning}
          />
          {editable && (
            <Status
              tone={state.tone}
              glyph={state.glyph}
              label={state.label}
              title={state.meaning}
            />
          )}
        </div>
        <p className="gc-muted" data-testid="draft-state-meaning">
          {editable ? state.meaning : lifecycle.meaning}
        </p>
      </div>

      {!editable && (
        <Notice tone="info" title="This version is approved and cannot be edited">
          An approved room is permanent, because anything configured against it must keep meaning
          what it meant. To change the room, start a new draft from this version.
        </Notice>
      )}

      {draft.status === 'stale' && (
        <Notice tone="critical" title="Someone else saved this draft first" live>
          <div className="gc-stack gc-stack--tight">
            <p>
              Your edits are still here and have not been sent. GateCheckPro will not write them
              over work you have not seen, and it will not try to combine the two — a merged seating
              plan is one nobody drew.
            </p>
            {draft.serverVersion !== null && (
              <p className="gc-muted" data-testid="stale-server-summary">
                Their version has {draft.serverVersion.seats.length.toLocaleString()} seats; yours
                has {draft.document.seats.length.toLocaleString()}.
              </p>
            )}
            <div className="gc-row">
              <Button variant="primary" onClick={() => void draft.discardAndReload()}>
                Discard mine and load theirs
              </Button>
            </div>
          </div>
        </Notice>
      )}

      <FailureNotice error={draft.status === 'stale' ? null : draft.error} />
      <FailureNotice error={publish.error} />

      <Card
        title="The room"
        eyebrow={`${draft.document.seats.length.toLocaleString()} seats`}
        action={
          editable ? (
            <div className="gc-row">
              <Button
                variant="primary"
                busy={draft.status === 'saving'}
                disabled={draft.status === 'persisted' || draft.status === 'saving'}
                onClick={() => void draft.save()}
              >
                Save the room
              </Button>
              <Button
                busy={publish.busy}
                onClick={() =>
                  void publish.run(`${versionPath}/publish`, { method: 'POST' }).then((result) => {
                    if (result !== null) window.location.reload();
                  })
                }
              >
                Approve this version
              </Button>
            </div>
          ) : undefined
        }
      >
        {draft.document.sections.length === 0 ? (
          <EmptyState title="An empty room">
            A map needs at least one section before it can be saved. A section is a stand, a tier or
            a floor — the parts a person would name when describing the room out loud.
          </EmptyState>
        ) : (
          <VenueMapCanvas
            topology={draft.document}
            selectedSeatKeys={selected}
            onSeatToggle={
              editable
                ? (seatKey) =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (next.has(seatKey)) next.delete(seatKey);
                      else next.add(seatKey);
                      return next;
                    })
                : undefined
            }
          />
        )}
      </Card>

      {editable && (
        <>
          <Card title="Add a section" eyebrow="A stand, a tier, a floor">
            <form
              className="gc-stack"
              onSubmit={(formEvent) => {
                formEvent.preventDefault();
                const index = draft.document.sections.length;
                draft.edit((current) =>
                  addSection(current, {
                    code: sectionCode,
                    displayName: sectionName,
                    kind: sectionKind,
                    // Laid out on a deterministic grid from the section index,
                    // so the same sequence of edits produces the same room on
                    // any machine.
                    x: 100 + (index % 2) * 1400,
                    y: 100 + Math.floor(index / 2) * 900,
                    width: 1200,
                    height: 700,
                  }),
                );
                setSectionCode('');
                setSectionName('');
              }}
            >
              <div className="gc-grid">
                <Field
                  label="Section code"
                  placeholder="NORTH"
                  required
                  error={codeProblem}
                  value={sectionCode}
                  onChange={(input) => setSectionCode(input.target.value.toUpperCase())}
                />
                <Field
                  label="Section name"
                  placeholder="North stand"
                  required
                  value={sectionName}
                  onChange={(input) => setSectionName(input.target.value)}
                />
                <div className="gc-field">
                  <label className="gc-field__label" htmlFor="sectionKind">
                    What is in it
                  </label>
                  <select
                    id="sectionKind"
                    className="gc-select"
                    value={sectionKind}
                    onChange={(input) => setSectionKind(input.target.value as SectionKind)}
                  >
                    <option value="SEATED">Seated — rows of chairs</option>
                    <option value="TABLE">Tables — premium seating around furniture</option>
                    <option value="STANDING">Standing — no numbered places</option>
                  </select>
                  <span className="gc-field__hint">
                    Permanent for this version, and enforced: a chair at a table cannot sit in a
                    seated section.
                  </span>
                </div>
              </div>
              <Button
                type="submit"
                variant="primary"
                disabled={sectionCode === '' || sectionName === '' || codeProblem !== undefined}
              >
                Add section
              </Button>
            </form>
          </Card>

          {seatedSections.length > 0 && (
            <Card title="Add rows of seats" eyebrow="Into a seated section">
              <form
                className="gc-stack"
                onSubmit={(formEvent) => {
                  formEvent.preventDefault();
                  const section =
                    seatedSections.find((candidate) => candidate.code === activeSection) ??
                    seatedSections[0];
                  if (section === undefined) return;
                  const bounds = section.geometry;
                  const originX = bounds?.kind === 'RECT' ? bounds.x + 60 : 100;
                  const originY = bounds?.kind === 'RECT' ? bounds.y + 80 : 100;
                  draft.edit((current) =>
                    addRowBlock(current, {
                      sectionCode: section.code,
                      rowCount: Number(rowCount),
                      seatsPerRow: Number(seatsPerRow),
                      originX,
                      originY: originY + section.rows.length * 40,
                      rowSpacing: 40,
                      seatSpacing: 40,
                    }),
                  );
                }}
              >
                <div className="gc-grid">
                  <div className="gc-field">
                    <label className="gc-field__label" htmlFor="targetSection">
                      Section
                    </label>
                    <select
                      id="targetSection"
                      className="gc-select"
                      value={
                        seatedSections.some((section) => section.code === activeSection)
                          ? activeSection
                          : (seatedSections[0]?.code ?? '')
                      }
                      onChange={(input) => setTarget(input.target.value)}
                    >
                      {seatedSections.map((section) => (
                        <option key={section.code} value={section.code}>
                          {section.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Field
                    label="Rows"
                    type="number"
                    min={1}
                    max={200}
                    value={rowCount}
                    onChange={(input) => setRowCount(input.target.value)}
                  />
                  <Field
                    label="Seats per row"
                    type="number"
                    min={1}
                    max={200}
                    value={seatsPerRow}
                    onChange={(input) => setSeatsPerRow(input.target.value)}
                  />
                </div>
                <Button type="submit" variant="primary">
                  Add rows
                </Button>
              </form>
            </Card>
          )}

          {tableSections.length > 0 && (
            <Card title="Add a table" eyebrow="Into a table section">
              <form
                className="gc-row"
                onSubmit={(formEvent) => {
                  formEvent.preventDefault();
                  const section =
                    tableSections.find((candidate) => candidate.code === activeSection) ??
                    tableSections[0];
                  if (section === undefined) return;
                  const bounds = section.geometry;
                  const index = section.tables.length;
                  draft.edit((current) =>
                    addTable(current, {
                      sectionCode: section.code,
                      code: tableCode,
                      displayName: `Table ${tableCode}`,
                      seatCapacity: 8,
                      x: (bounds?.kind === 'RECT' ? bounds.x + 200 : 200) + (index % 4) * 300,
                      y:
                        (bounds?.kind === 'RECT' ? bounds.y + 500 : 500) +
                        Math.floor(index / 4) * 300,
                      radius: 80,
                    }),
                  );
                  setTableCode('');
                }}
              >
                <div style={{ flex: '1 1 12rem' }}>
                  <Field
                    label="Table code"
                    placeholder="T1"
                    value={tableCode}
                    onChange={(input) => setTableCode(input.target.value.toUpperCase())}
                  />
                </div>
                <Button type="submit" disabled={tableCode === ''}>
                  Add a table of 8
                </Button>
              </form>
            </Card>
          )}

          <Card title="Group the selected seats" eyebrow={`${String(selected.size)} selected`}>
            <form
              className="gc-row"
              onSubmit={(formEvent) => {
                formEvent.preventDefault();
                draft.edit((current) =>
                  addSeatGroup(current, {
                    code: groupCode,
                    displayName: `Box ${groupCode}`,
                    kind: 'BOX',
                    seatKeys: [...selected],
                  }),
                );
                setGroupCode('');
                setSelected(new Set());
              }}
            >
              <div style={{ flex: '1 1 12rem' }}>
                <Field
                  label="Group code"
                  placeholder="BOX_A"
                  hint="A box, a suite or a pod. Membership only — it prices nothing."
                  value={groupCode}
                  onChange={(input) => setGroupCode(input.target.value.toUpperCase())}
                />
              </div>
              <Button type="submit" disabled={groupCode === '' || selected.size === 0}>
                Group {selected.size} seats
              </Button>
            </form>
          </Card>

          <Card title="Sections" eyebrow="What the room is made of">
            <div className="gc-table-scroll">
              <table className="gc-table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Name</th>
                    <th scope="col">Rows</th>
                    <th scope="col">Tables</th>
                    <th scope="col">Seats</th>
                    <th scope="col">
                      <span className="gc-visually-hidden">Remove</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {draft.document.sections.map((section) => (
                    <tr key={section.code}>
                      <td className="gc-mono">{section.code}</td>
                      <td>{section.displayName}</td>
                      <td className="gc-mono">{section.rows.length}</td>
                      <td className="gc-mono">{section.tables.length}</td>
                      <td className="gc-mono">
                        {draft.document.seats
                          .filter((seat) => seat.sectionCode === section.code)
                          .length.toLocaleString()}
                      </td>
                      <td>
                        <Button
                          onClick={() =>
                            draft.edit((current) => removeSection(current, section.code))
                          }
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {map.resource.status === 'loaded' && (
        <p className="gc-muted gc-mono" data-testid="editor-digest">
          Saved digest {draft.expectedDigest.slice(0, 12)}
        </p>
      )}
    </div>
  );
}
